import { useEffect } from "preact/hooks";

import { authState, DEV_BYPASS, initAuth, signOut } from "@/auth/store";
import { HeaderStatus } from "@/components/HeaderStatus";
import { LangToggle } from "@/components/LangToggle";
import { TenantSelector } from "@/components/TenantSelector";
import { WindowSelector } from "@/components/WindowSelector";
import { t } from "@/i18n";
import {
  analystCheck,
  currentView,
  includeTestSessions,
  TENANT_SCOPED_VIEWS,
  VIEW_WINDOWS,
} from "@/state/filters";
import { openSection, SECTIONS, sectionOf } from "@/state/nav";
import { AggregatesView } from "@/views/AggregatesView";
import { AnalystView } from "@/views/AnalystView";
import { ConfigView } from "@/views/ConfigView";
import { CostView } from "@/views/CostView";
import { CustomersView } from "@/views/CustomersView";
import { EngagementView } from "@/views/EngagementView";
import { EstimateView } from "@/views/EstimateView";
import { EventsView } from "@/views/EventsView";
import { GlossaryView } from "@/views/GlossaryView";
import { HealthView } from "@/views/HealthView";
import { InsightView } from "@/views/InsightView";
import { KpiScorecardView } from "@/views/KpiScorecardView";
import { Login } from "@/views/Login";
import { OpsView } from "@/views/OpsView";
import { PartnerReportView } from "@/views/PartnerReportView";
import { PeakView } from "@/views/PeakView";
import { PromptsView } from "@/views/PromptsView";
import { PulseView } from "@/views/PulseView";
import { QualityView } from "@/views/QualityView";
import { QuestionCheckView } from "@/views/QuestionCheckView";
import { RecentQAView } from "@/views/RecentQAView";
import { SessionInspectorView } from "@/views/SessionInspectorView";
import { SignalsView } from "@/views/SignalsView";
import { TalentGeoView } from "@/views/TalentGeoView";
import { TestPageView } from "@/views/TestPageView";

export function App() {
  useEffect(() => {
    initAuth();
  }, []);

  const auth = authState.value;
  if (auth.status === "loading") {
    return (
      <div class="login-screen">
        <div class="login-card">{t("app.loading")}</div>
      </div>
    );
  }
  if (auth.status !== "signedIn") {
    return <Login />;
  }

  const view = currentView.value;
  // The analysis chat over a Fragen-Check reads neither the window nor the
  // partner filter: the check is its own scope.
  const checkScoped = view === "analyst" && analystCheck.value !== null;
  const viewWindow = checkScoped ? undefined : VIEW_WINDOWS[view];
  const tenantScoped = !checkScoped && TENANT_SCOPED_VIEWS.has(view);
  // The glossary belongs to no section: it opens from the header's «?».
  const section = sectionOf(view);
  return (
    <div class="layout">
      <aside class="layout-sidebar">
        <div class="brand">
          gateway<b>.one</b>
          <div style="font-weight: 400; font-size: 12px; opacity: 0.8; margin-top: 4px;">
            {t("app.title")}
          </div>
        </div>
        <nav class="nav-sections" aria-label={t("nav.sections")}>
          {SECTIONS.map((s) => {
            const active = s.id === section?.id;
            return (
              <button
                key={s.id}
                type="button"
                class={`nav-link ${active ? "active" : ""}`}
                aria-current={active ? "page" : undefined}
                onClick={() => openSection(s.id)}
                // The label collapses to icon-only on narrow viewports and under
                // page zoom, so it has to stay reachable as a tooltip.
                title={t(s.label)}
              >
                <span class="nav-icon" aria-hidden="true">
                  {s.icon}
                </span>
                <span class="nav-label">{t(s.label)}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      <header class="layout-header">
        <div class="header-row">
          <h1>{section ? t(section.label) : t("view.glossary")}</h1>
          <div class="header-controls">
            {/* One selector for the whole dashboard: it stays on screen while
                the main column scrolls, and every windowed view binds to it. */}
            {viewWindow && (
              <WindowSelector value={viewWindow.signal} options={viewWindow.options} />
            )}
            {/* Only above panels that actually apply the scope — rendering it
                elsewhere would imply a filter that isn't in effect. */}
            {tenantScoped && <TenantSelector />}
            <LangToggle />
            <div class="toolbar">
              <label class="toggle">
                <input
                  type="checkbox"
                  checked={includeTestSessions.value}
                  onInput={(e) =>
                    (includeTestSessions.value = (e.target as HTMLInputElement).checked)
                  }
                />
                <span>{t("header.includeTest")}</span>
              </label>
              {!DEV_BYPASS && (
                <button class="signout-btn" onClick={() => void signOut()}>
                  {t("header.signOut")}
                  {auth.user?.email ? ` · ${auth.user.email}` : ""}
                </button>
              )}
            </div>
            <HeaderStatus />
            <button
              type="button"
              class="header-help"
              aria-label={t("header.glossary")}
              title={t("header.glossary")}
              onClick={() => (currentView.value = "glossary")}
            >
              ?
            </button>
          </div>
        </div>
        {section && (
          <nav class="section-tabs" aria-label={t(section.label)}>
            {section.views.map((v) => (
              <button
                key={v}
                type="button"
                class={`section-tab ${v === view ? "active" : ""}`}
                aria-current={v === view ? "page" : undefined}
                onClick={() => (currentView.value = v)}
              >
                {t(`view.${v}`)}
              </button>
            ))}
          </nav>
        )}
      </header>

      <main class="layout-main">
        {view === "health" && <HealthView />}
        {view === "ops" && <OpsView />}
        {view === "customers" && <CustomersView />}
        {view === "testpage" && <TestPageView />}
        {view === "kpi" && <KpiScorecardView />}
        {view === "pulse" && <PulseView />}
        {view === "engagement" && <EngagementView />}
        {view === "peak" && <PeakView />}
        {view === "quality" && <QualityView />}
        {view === "signals" && <SignalsView />}
        {view === "questions" && <QuestionCheckView />}
        {view === "talentgeo" && <TalentGeoView />}
        {view === "cost" && <CostView />}
        {view === "estimate" && <EstimateView />}
        {view === "partner" && <PartnerReportView />}
        {view === "aggregates" && <AggregatesView />}
        {view === "qna" && <RecentQAView />}
        {view === "sessions" && <SessionInspectorView />}
        {view === "events" && <EventsView />}
        {view === "analyst" && <AnalystView />}
        {view === "insights" && <InsightView />}
        {view === "prompts" && <PromptsView />}
        {view === "config" && <ConfigView />}
        {view === "glossary" && <GlossaryView />}
      </main>
    </div>
  );
}
